import React, { useState } from 'react';
import { 
  formatPercent, 
  formatDelta, 
  getDeltaClass, 
  getDeltaStatus 
} from './utils/dataTransformations';
import { FaSortDown, FaSortUp, FaSort } from 'react-icons/fa';

const IndicatorTable = ({ 
  indicator, 
  title,
  description,
  data, 
  selectedYear, 
  showLegend = true // Option to hide legend
}) => {
  // State for sorting
  const [sortField, setSortField] = useState('none');
  const [sortDirection, setSortDirection] = useState('asc');

  // Find the year index for the selected year
  const yearIndex = data && Object.values(data)[0] ? 
    Object.values(data)[0].findIndex(d => d.year === selectedYear) : -1;

  // If no data or invalid year, show placeholder
  if (!data || Object.keys(data).length === 0 || yearIndex === -1) {
    return (
      <div className="bg-gray-50 p-4 rounded-lg text-center text-gray-500">
        No data available for {indicator}
      </div>
    );
  }

  // Group student groups by category
  const studentGroupsByCategory = {
    "ALL / GENDER": [],
    "RACE/ETHNICITY": [],
    "SPECIAL STUDENT GROUP": []
  };
  
  // Sort groups into categories
  Object.keys(data).forEach(group => {
    if (group === "All Students" || group === "Female" || group === "Male") {
      studentGroupsByCategory["ALL / GENDER"].push(group);
    } else if (group.includes("English") || group.includes("Disabilities") || 
             group.includes("Income") || group.includes("Homeless") || 
             group.includes("Migrant") || group.includes("Section 504")) {
      studentGroupsByCategory["SPECIAL STUDENT GROUP"].push(group);
    } else {
      studentGroupsByCategory["RACE/ETHNICITY"].push(group);
    }
  });

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sort icon
  const getSortIcon = (field) => {
    if (sortField !== field) return <FaSort className="inline ml-1 text-gray-400" />;
    return sortDirection === 'asc' ? 
      <FaSortUp className="inline ml-1 text-blue-500" /> : 
      <FaSortDown className="inline ml-1 text-blue-500" />;
  };

  // Sort function for groups
  const sortGroups = (groups) => {
    if (sortField === 'none') return groups;
    
    return [...groups].sort((a, b) => {
      const dataA = data[a][yearIndex];
      const dataB = data[b][yearIndex];
      
      let valueA, valueB;
      
      switch (sortField) {
        case 'group':
          valueA = a;
          valueB = b;
          break;
        case 'actual':
          valueA = dataA.actual === -10 ? -999 : dataA.actual;
          valueB = dataB.actual === -10 ? -999 : dataB.actual;
          break;
        case 'delta':
          valueA = dataA.delta === -10 ? -999 : dataA.delta;
          valueB = dataB.delta === -10 ? -999 : dataB.delta;
          break;
        default:
          return 0;
      }
      
      // Handle nulls and -10 (masked)
      if (valueA === null) return 1;
      if (valueB === null) return -1;
      
      // Sort direction
      return sortDirection === 'asc' ? 
        valueA - valueB : 
        valueB - valueA;
    });
  };

  // Threshold for significant performance gap (10% below target)
  const SIGNIFICANT_GAP_THRESHOLD = -0.1;
  // Threshold for moderate performance gap (5% below target)
  const MODERATE_GAP_THRESHOLD = -0.05;

  return (
    <div className="bg-white h-full flex flex-col">
      {/* Target and year summary */}
      <div className="p-2 bg-gray-50 border-b border-gray-200">
        <div className="flex justify-between text-xs text-gray-600">
          <span>Year: {selectedYear}</span>
          <span>Target: {data?.[Object.keys(data)[0]]?.[yearIndex]?.target ? 
            formatPercent(data[Object.keys(data)[0]][yearIndex].target) : 'N/A'}</span>
        </div>
      </div>
      
      {/* Table content */}
      <div className="overflow-y-auto flex-grow">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th 
                scope="col" 
                className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('group')}
              >
                Student Group {getSortIcon('group')}
              </th>
              <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Met
              </th>
              <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                N
              </th>
              <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                D
              </th>
              <th 
                scope="col" 
                className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('actual')}
              >
                Actual% {getSortIcon('actual')}
              </th>
              <th 
                scope="col" 
                className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('delta')}
              >
                Delta {getSortIcon('delta')}
              </th>
            </tr>
          </thead>
          
          {/* Render each category */}
          {Object.entries(studentGroupsByCategory).map(([category, unsortedGroups]) => {
            // Apply sorting
            const groups = sortGroups(unsortedGroups);
            
            return groups.length > 0 ? (
              <React.Fragment key={`${indicator}-${category}`}>
                <tbody className="bg-white divide-y divide-gray-200">
                  {/* Category header */}
                  <tr className="bg-gray-50">
                    <td colSpan={6} className="px-3 py-1 text-xs font-medium text-gray-700">
                      {category}
                    </td>
                  </tr>
                  
                  {/* Student groups in this category */}
                  {groups.map(group => {
                    const rowData = data[group][yearIndex];
                    
                    // Determine background based on performance gap
                    let bgClass = "";
                    if (!rowData.isMasked && rowData.delta !== null) {
                      if (rowData.delta < SIGNIFICANT_GAP_THRESHOLD) {
                        bgClass = "bg-orange-50"; // Significant gap
                      } else if (rowData.delta < MODERATE_GAP_THRESHOLD) {
                        bgClass = "bg-yellow-50"; // Moderate gap
                      }
                    }
                    
                    return (
                      <tr 
                        key={`${indicator}-${group}`}
                        className={`hover:bg-gray-50 ${bgClass}`}
                      >
                        <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900">
                          {group}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-center">
                          {rowData.isMasked ? (
                            <span className="inline-block w-4 h-4 text-gray-400">✕</span>
                          ) : rowData.met ? (
                            <span className="inline-block w-4 h-4 text-green-500">✓</span>
                          ) : (
                            <span className="inline-block w-4 h-4 text-gray-400">✕</span>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-center text-gray-500">
                          {rowData.isMasked ? '-10' : rowData.numerator}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-center text-gray-500">
                          {rowData.isMasked ? '-10' : rowData.denominator}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-center">
                          {formatPercent(rowData.actual)}
                        </td>
                        <td className={`px-3 py-2 whitespace-nowrap text-xs text-center ${getDeltaClass(rowData.delta)}`}>
                          <div className="flex flex-col items-center">
                            <span>{formatDelta(rowData.delta)}</span>
                            {!rowData.isMasked && rowData.delta !== null && (
                              <span className="text-[10px] text-gray-600 mt-0.5">{getDeltaStatus(rowData.delta)}</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </React.Fragment>
            ) : null;
          })}
        </table>
      </div>

      {/* Legend (optional) */}
      {showLegend && (
        <div className="p-2 border-t border-gray-200 bg-gray-50 mt-auto">
          <div className="flex flex-wrap justify-between gap-2 text-xs">
            <div>
              <h4 className="font-medium mb-1">Performance Status</h4>
              <div className="flex flex-wrap gap-2">
                <div className="flex items-center">
                  <span className="inline-block w-3 h-3 bg-orange-300 mr-1"></span>
                  <span className="text-gray-600">Below Target</span>
                </div>
                <div className="flex items-center">
                  <span className="inline-block w-3 h-3 bg-blue-200 mr-1"></span>
                  <span className="text-gray-600">At/Above Target</span>
                </div>
                <div className="flex items-center">
                  <span className="inline-block w-3 h-3 bg-orange-200 mr-1"></span>
                  <span className="text-gray-600">Masked Data</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-1">Gap Highlighting</h4>
              <div className="flex flex-wrap gap-2">
                <div className="flex items-center">
                  <span className="inline-block w-3 h-3 bg-orange-50 mr-1"></span>
                  <span className="text-gray-600">Significant Gap (>10%)</span>
                </div>
                <div className="flex items-center">
                  <span className="inline-block w-3 h-3 bg-yellow-50 mr-1"></span>
                  <span className="text-gray-600">Moderate Gap (5-10%)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndicatorTable;